/**
 * PrintDocument — the A4 document frame of the pharmacy print layer (refonte WP-19 · F4,
 * design/ux/01_foundations.md §3.16 « A4 documents »).
 *
 * Facture d'achat, avoir fournisseur, bon de commande, bon de retour, réclamation, bordereau,
 * journals (landscape), Trésorerie's situation / relevé / avis de règlement: every A4 document is
 * this frame plus its own body. The frame owns the header (pharmacy block with its logo, document
 * title, number, date, status IN WORDS, the tiers block), the totals block that never splits across
 * pages, the notes and the footer (printed through the @page margin boxes by the print root:
 * « Imprimé le … par … » and « Page X / Y »; previewed on screen at the bottom of the paper).
 *
 * Its root carries `data-print-doc`, `data-print-page` (a4-portrait / a4-landscape) and
 * `data-print-title`, which is all PrintRoot / `printDocNode` need. Every value is rendered as
 * React text (escaped); nothing is injected as HTML.
 *
 *   <PrintRoot ref={printRef} preview>
 *     <PrintDocument
 *       title="Facture d'achat" numero="FA-2026-01542" date={facture.date_facture}
 *       status="facture_achat.VALIDE" pharmacie={pharmacie}
 *       tiers={{ role: "Fournisseur", nom: f.raison_sociale, lignes: [f.adresse, `NIF ${f.nif}`] }}
 *       totals={[{ label: "Total HT", value: t.total_ht }, …, { label: "Total TTC", value: t.ttc, strong: true }]}
 *     >
 *       <PrintTable columns={…} rows={lignes} getRowId={(l) => l.id} />
 *     </PrintDocument>
 *   </PrintRoot>
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { assetUrl } from "@/config/app.config.js";
import { fmtDate, fmtMoney } from "@/config/locale.config.js";
import { resolveStatus } from "@/config/status.config.js";
import { formatDecimal } from "@/lib/number-format.js";
import { PRINT_LABELS, pageA4, pageVars } from "@/config/print.config.js";
import { footerLine } from "@/lib/print-frame.js";

export interface PrintPharmacie {
  nom?: string | null;
  adresse?: string | null;
  wilaya?: string | null;
  telephone?: string | null;
  email?: string | null;
  logo_url?: string | null;
  numero_agrement?: string | null;
  pied_recu?: string | null;
}

export interface PrintTiers {
  /** « Fournisseur », « Client », « Assuré »… */
  role: string;
  nom: string;
  /** Address lines, identifiers (NIF / RC), contact — empty values are dropped. */
  lignes?: Array<string | null | undefined>;
}

export interface PrintMeta {
  label: string;
  value: React.ReactNode;
}

export interface PrintTotal {
  label: string;
  /** Decimal string / number (money by default). */
  value: unknown;
  kind?: "money" | "qty" | "text";
  strong?: boolean;
  signed?: boolean;
  /** Hide the row when the value is null / zero (e.g. « Remise globale » 0). */
  hideWhenEmpty?: boolean;
}

export interface PrintDocumentProps {
  /** The printed title (« Bon de commande »), uppercased by the stylesheet. */
  title: string;
  numero?: string | null;
  date?: string | Date | null;
  /** A status registry key (« facture_achat.VALIDE ») or the words themselves. */
  status?: string | null;
  /** Override of the status words (when the registry has no entry). */
  statusText?: string | null;
  pharmacie?: PrintPharmacie | null;
  tiers?: PrintTiers | null;
  /** Further identity rows under number / date / status (« Échéance », « N° fournisseur »). */
  meta?: PrintMeta[];
  totals?: PrintTotal[];
  notes?: string | null;
  orientation?: "portrait" | "landscape";
  /** Window title of the print (defaults to « title numero »). */
  printTitle?: string;
  /** Screen-preview footer (the print uses the @page margin boxes). */
  printedBy?: string | null;
  printedAt?: Date;
  children?: React.ReactNode;
  className?: string;
}

/** Status words of a registry key; an unknown key prints its raw value (never a colour). */
export function statusWords(status?: string | null, statusText?: string | null): string {
  if (statusText) return statusText;
  if (!status) return "";
  const r = status.includes(".") ? resolveStatus(status) : null;
  return r?.label ?? (status.includes(".") ? status.split(".").pop() ?? "" : status);
}

function formatTotal(t: PrintTotal): string {
  if (t.kind === "text") return String(t.value ?? "");
  if (t.kind === "qty") return formatDecimal(t.value, { decimals: 0 });
  return fmtMoney(t.value, { signed: t.signed });
}

const isEmptyValue = (v: unknown) =>
  v == null || v === "" || (typeof v !== "object" && Number(v) === 0 && !Number.isNaN(Number(v)));

export function PrintDocument({
  title,
  numero,
  date,
  status,
  statusText,
  pharmacie,
  tiers,
  meta = [],
  totals = [],
  notes,
  orientation = "portrait",
  printTitle,
  printedBy,
  printedAt,
  children,
  className,
}: PrintDocumentProps) {
  const page = pageA4(orientation);
  const words = statusWords(status, statusText);
  const logo = assetUrl(pharmacie?.logo_url ?? "");
  const lieu = [pharmacie?.adresse, pharmacie?.wilaya].filter(Boolean).join(", ");
  const contact = [
    pharmacie?.telephone ? `${PRINT_LABELS.telephone} ${pharmacie.telephone}` : null,
    pharmacie?.email,
  ]
    .filter(Boolean)
    .join(" · ");
  const shownTotals = totals.filter((t) => !(t.hideWhenEmpty && isEmptyValue(t.value)));
  const tiersLignes = (tiers?.lignes ?? []).filter((l) => l != null && String(l).trim() !== "");

  return (
    <article
      className={cn("pk-pdoc", `pk-pdoc--${orientation}`, className)}
      data-print-doc=""
      data-print-page={page}
      data-print-title={printTitle ?? [title, numero].filter(Boolean).join(" ")}
      style={pageVars(page) as React.CSSProperties}
      lang="fr"
    >
      <header className="pk-pdoc-head">
        <div className="pk-pdoc-pharmacie">
          {logo ? <img className="pk-pdoc-logo" src={logo} alt="" /> : null}
          {pharmacie ? (
            <div>
              {pharmacie.nom ? <p className="pk-pdoc-pharmacie-nom">{pharmacie.nom}</p> : null}
              {lieu ? <p>{lieu}</p> : null}
              {contact ? <p>{contact}</p> : null}
              {pharmacie.numero_agrement ? (
                <p>
                  {PRINT_LABELS.agrement} {pharmacie.numero_agrement}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="pk-pdoc-ident">
          <h1 className="pk-pdoc-titre">{title}</h1>
          <dl className="pk-pdoc-meta">
            {numero ? (
              <>
                <dt>{PRINT_LABELS.numero}</dt>
                <dd>{numero}</dd>
              </>
            ) : null}
            {date ? (
              <>
                <dt>{PRINT_LABELS.date}</dt>
                <dd>{fmtDate(date)}</dd>
              </>
            ) : null}
            {words ? (
              <>
                <dt>{PRINT_LABELS.statut}</dt>
                <dd data-print-status="">{words}</dd>
              </>
            ) : null}
            {meta.map((m) => (
              <React.Fragment key={m.label}>
                <dt>{m.label}</dt>
                <dd>{m.value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      </header>

      {tiers ? (
        <section className="pk-pdoc-tiers" aria-label={tiers.role}>
          <p className="pk-pdoc-tiers-role">{tiers.role}</p>
          <p className="pk-pdoc-tiers-nom">{tiers.nom}</p>
          {tiersLignes.map((l, i) => (
            <p key={i}>{l}</p>
          ))}
        </section>
      ) : null}

      <div className="pk-pdoc-body">{children}</div>

      {shownTotals.length > 0 ? (
        <table className="pk-pdoc-totaux" aria-label={PRINT_LABELS.totaux}>
          <tbody>
            {shownTotals.map((t) => (
              <tr key={t.label} className={cn(t.strong && "pk-ptotal-fort")}>
                <th scope="row">{t.label}</th>
                <td>{formatTotal(t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {notes ? <div className="pk-pdoc-notes">{notes}</div> : null}
      {pharmacie?.pied_recu ? <p className="pk-pdoc-mentions">{pharmacie.pied_recu}</p> : null}

      <footer className="pk-pdoc-foot" aria-hidden="true">
        {/* « Page X / Y » exists only on paper (@page counters): a screen « 1 / 1 » would lie. */}
        <span>{footerLine({ printedAt, printedBy: printedBy ?? undefined })}</span>
      </footer>
    </article>
  );
}

export interface PrintColumn<R> {
  id: string;
  label: string;
  /** money / qty / number right-aligned with tabular numerals; code in the mono face. */
  kind?: "text" | "money" | "qty" | "number" | "percent" | "date" | "code";
  value?: (row: R) => unknown;
  /** Custom cell (rendered as React text — never HTML). */
  cell?: (row: R) => React.ReactNode;
  width?: string;
  decimals?: number;
}

export interface PrintTableProps<R> {
  columns: PrintColumn<R>[];
  rows: R[];
  getRowId: (row: R) => string | number;
  caption?: string;
  /** A footer row (e.g. a journal's Σ per column), aligned to the columns by id. */
  footer?: Record<string, React.ReactNode>;
  emptyText?: string;
  className?: string;
}

const NUMERIC = new Set(["money", "qty", "number", "percent"]);

function formatCell<R>(c: PrintColumn<R>, row: R): React.ReactNode {
  if (c.cell) return c.cell(row);
  const v = c.value ? c.value(row) : (row as any)?.[c.id];
  switch (c.kind) {
    case "money":
      return fmtMoney(v, { noSuffix: true, decimals: c.decimals });
    case "qty":
      return formatDecimal(v, { decimals: c.decimals ?? 0 });
    case "number":
      return formatDecimal(v, { decimals: c.decimals ?? 2 });
    case "percent": {
      const s = formatDecimal(v, { decimals: c.decimals ?? 2 });
      return s === "—" ? s : `${s} %`;
    }
    case "date":
      return fmtDate(v as any);
    default:
      return v == null || v === "" ? "—" : String(v);
  }
}

/**
 * The printed table: header repeated on every page, rows never split, amounts right-aligned. A
 * money column's header should carry « (DA) » (the cells print without the suffix, P-06).
 */
export function PrintTable<R>({ columns, rows, getRowId, caption, footer, emptyText, className }: PrintTableProps<R>) {
  const cls = (c: PrintColumn<R>) =>
    cn(NUMERIC.has(c.kind ?? "text") && "pk-pnum", c.kind === "code" && "pk-pcode");
  return (
    <table className={cn("pk-ptable", className)}>
      {caption ? <caption className="pk-pdoc-section-titre">{caption}</caption> : null}
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.id} scope="col" className={cls(c)} style={c.width ? { width: c.width } : undefined}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr className="pk-ptable-vide">
            <td colSpan={columns.length}>{emptyText ?? PRINT_LABELS.aucuneLigne}</td>
          </tr>
        ) : (
          rows.map((r) => (
            <tr key={getRowId(r)}>
              {columns.map((c) => (
                <td key={c.id} className={cls(c)}>
                  {formatCell(c, r)}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
      {footer ? (
        <tfoot>
          <tr>
            {columns.map((c) => (
              <th key={c.id} scope="row" className={cls(c)}>
                {footer[c.id] ?? ""}
              </th>
            ))}
          </tr>
        </tfoot>
      ) : null}
    </table>
  );
}

export default PrintDocument;
